'use client'

// The in-app browser RECOVERY — shown after a provider sign-in came back empty
// from inside a webview (Instagram, X, Gmail, …), never before one is tried.
//
// ⚠️ THIS USED TO BE A GATE and it was the bug. It rendered the moment we
// detected a webview, announced "google sign-in is blocked", and OAuthButtons
// refused to even attempt the redirect — so a flow that works for other sites
// inside X was guaranteed to fail for us, and we told the user it was Google's
// doing. It is not our place to predict another company's policy; it is our
// place to notice when something actually failed and help.
//
// The trigger is now the breadcrumb from lib/inAppBrowser: set immediately
// before handing over to a provider, read on the way back. Crumb + still in a
// webview = they went to Google/GitHub and returned without a session, which is
// what the block looks like from here. Anything less than that shows nothing.
//
// Coming back is usually a BACK-BUTTON restore, so mount alone is not enough to
// catch it — `pageshow` fires for bfcache restores where React never remounts.

import { useCallback, useEffect, useState } from 'react'
import { detectInAppBrowser, escapeToBrowser, takeReturnedOAuthAttempt, type InAppInfo } from '../lib/inAppBrowser'
import { carryAttribution } from '../lib/utm'
import { createClient } from '../lib/supabase/client'
import { siteUrl } from '../lib/site-url'

export default function InAppBrowserNotice({ tone = 'light' }: { tone?: 'light' | 'dark' }) {
  const [info, setInfo] = useState<InAppInfo | null>(null)
  const [provider, setProvider] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [tried, setTried] = useState(false)

  // Detection is client-only (reads navigator.userAgent). Both the crumb and
  // the webview test have to pass — outside a webview a failed sign-in is an
  // ordinary error and belongs to the caller's error line, not to this notice.
  // ⚠️ "WITHOUT A SESSION" IS CHECKED, NOT ASSUMED. A successful sign-in leaves
  // the crumb behind too — /auth/callback is a server route, so it cannot clear
  // a sessionStorage key — and the user may well land back here afterwards. Ask
  // Supabase whether a session exists before crying failure, or the notice
  // greets people who just signed in perfectly well.
  const check = useCallback(async () => {
    const i = detectInAppBrowser()
    if (!i.isInApp) return
    // TWO TRIGGERS, EITHER SUFFICIENT.
    //  · ?recover=inapp — the SERVER saw the exchange fail for a missing PKCE
    //    verifier, which means the round trip ended in a different browser than
    //    it began. Authoritative, and it survives the hand-off that loses the
    //    crumb (that hand-off is the whole failure mode, so the crumb cannot be
    //    the only signal — that was the gap this closes).
    //  · the sessionStorage crumb — a provider attempt that came back with
    //    nothing at all, where no code ever reached the callback.
    const served = new URLSearchParams(window.location.search).get('recover') === 'inapp'
    const p = takeReturnedOAuthAttempt() ?? (served ? 'that' : null)
    if (!p) return
    try {
      const { data } = await createClient().auth.getSession()
      if (data.session) return // it worked — the crumb was just litter
    } catch {
      // no client / no config: fall through and offer the recovery anyway
    }
    setInfo(i)
    setProvider(p)
  }, [])

  useEffect(() => {
    // `set-state-in-effect` assumes state can be derived during render. It
    // cannot here: every input (navigator.userAgent, sessionStorage, the auth
    // session) is browser-only, so reading it during render would either crash
    // SSR or hydrate a notice the server never drew. Post-mount is the only
    // correct moment. (The rule already failed on this line before the rewrite;
    // it is answered now rather than inherited.)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void check()
    const onShow = () => void check()
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [check])

  if (!info) return null

  // the ad's tags and X's click id go with them to the other browser (lib/utm carryAttribution)
  const url = typeof window !== 'undefined' ? carryAttribution(window.location.href) : siteUrl()

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2200)
    } catch {
      // clipboard blocked in some webviews — the instructions still apply
      setCopied(false)
    }
  }

  // The escape is an ATTEMPT on both platforms (see escapeToBrowser): Android's
  // intent:// is reliable, iOS's x-safari-https:// is version-dependent and can
  // silently do nothing. So the manual instructions stay on screen either way,
  // and the button admits it tried rather than claiming it worked.
  const escape = () => {
    setTried(true)
    escapeToBrowser(url)
  }

  const appName = info.app ?? 'this app'
  const openHint = info.platform === 'ios' ? 'tap the ⋯ menu, then “open in browser”' : 'tap the ⋮ menu, then “open in browser”'
  const escapeLabel = info.platform === 'ios' ? 'open in safari' : 'open in chrome'
  const canEscape = info.platform === 'ios' || info.platform === 'android'

  const s = styles(tone)

  return (
    <div role="alert" data-inapp-notice style={s.wrap}>
      <p style={s.lead}>that didn’t go through inside {appName}.</p>
      <p style={s.body}>
        some apps’ built-in browsers are refused by sign-in providers. open innernet.live in your own browser and it
        will work: {openHint}. or use the email option below, which works right here.
      </p>
      {tried && (
        <p style={s.body}>
          if nothing opened, {appName} is holding onto the link. please copy it and paste it into safari or chrome.
        </p>
      )}
      <div style={s.row}>
        {canEscape && (
          <button type="button" style={s.btn} onClick={escape}>
            {escapeLabel}
          </button>
        )}
        <button type="button" style={s.btnGhost} onClick={copy}>
          {copied ? 'link copied ✓' : 'copy link'}
        </button>
      </div>
      {provider && provider !== 'that' && (
        <span style={s.foot}>{provider} sign-in came back without a session</span>
      )}
    </div>
  )
}

// The notice sits on both light (/login) and dark-glass (BeginFlow) surfaces, so
// every colour is derived from one base ink — white on dark, --ink on light.
//
// ⚠️ THE SOLID BUTTON IS --ink ON --bg, NEVER --ink ON A SECOND INK. Its text used
// to be `var(--paper, #fff)`, and `--paper` is defined nowhere, so it was always
// #fff. Inside the auth room (`.auth-night`) `--ink` is itself near-white, which
// made "open in safari" white text on a white pill: the one button that recovers
// a person from a refused Google/GitHub sign-in inside LinkedIn, X or Gmail was
// invisible. `--bg` is the ground the notice stands on in every room (the deep
// night blue on the door, cream elsewhere), and ink-on-ground is the pair every
// surface already keeps legible, so the pill inverts whatever it sits on: measured
// 9.05:1 on the night door and 14.03:1 on cream (checked in a browser, both).
function styles(tone: 'light' | 'dark'): Record<string, React.CSSProperties> {
  const ink = tone === 'dark' ? '#fff' : 'var(--ink)'
  const paper = tone === 'dark' ? '#0a0e18' : 'var(--bg, #fffaf1)'
  const mix = (pct: number) => `color-mix(in oklab, ${ink} ${pct}%, transparent)`
  return {
    wrap: {
      border: `1px solid ${mix(16)}`,
      background: mix(tone === 'dark' ? 8 : 4),
      borderRadius: 14,
      padding: '0.9rem 1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      fontSize: '0.86rem',
      lineHeight: 1.5,
    },
    lead: { margin: 0, color: ink, fontWeight: 500 },
    body: { margin: 0, color: mix(72) },
    row: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.15rem' },
    foot: { color: mix(45), fontSize: '0.74rem' },
    btn: {
      appearance: 'none',
      border: 'none',
      borderRadius: 999,
      padding: '0.5rem 1rem',
      background: ink,
      color: paper,
      fontSize: '0.84rem',
      cursor: 'pointer',
    },
    btnGhost: {
      appearance: 'none',
      borderRadius: 999,
      padding: '0.5rem 1rem',
      background: 'transparent',
      border: `1px solid ${mix(24)}`,
      color: ink,
      fontSize: '0.84rem',
      cursor: 'pointer',
    },
  }
}
